/**
 * React Query hooks for LLM scan sharing functionality.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  accessShare,
  createShare,
  getSharedScanResults,
  listShares,
  revokeShare,
  validateShareToken,
} from "@/services/shareService";
import type { ShareCreateRequest } from "@/types/llm";

/**
 * Hook to create a shareable link for a scan.
 */
export function useCreateShare(scanUuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ShareCreateRequest) => createShare(scanUuid, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["scan-shares", scanUuid] });
    },
  });
}

/**
 * Hook to fetch all shares for a scan.
 */
export function useScanShares(scanUuid: string) {
  return useQuery({
    queryKey: ["scan-shares", scanUuid],
    queryFn: () => listShares(scanUuid),
    enabled: !!scanUuid,
  });
}

/**
 * Hook to revoke a share.
 */
export function useRevokeShare(scanUuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shareUuid: string) => revokeShare(shareUuid),
    onSuccess: () => {
      // Invalidate shares list to refresh
      queryClient.invalidateQueries({ queryKey: ["scan-shares", scanUuid] });
    },
  });
}

/**
 * Hook to validate a share token (public, no auth).
 */
export function useValidateShareToken(token: string) {
  return useQuery({
    queryKey: ["share-token", token],
    queryFn: () => validateShareToken(token),
    enabled: !!token,
  });
}

/**
 * Hook to access a shared scan summary (public, no auth).
 * Backend accepts null for non-password-protected shares.
 */
export function useAccessShare(token: string, password?: string | null) {
  return useQuery({
    queryKey: ["shared-scan-summary", token, password],
    queryFn: () => accessShare(token, password ?? undefined),
    enabled: !!token,
  });
}

/**
 * Hook to fetch results for a shared scan (public, no auth).
 * Backend accepts null for non-password-protected shares.
 * Supports server-side filtering by status, severity, plugin_id, and search query.
 */
export function useSharedScanResults(
  token: string,
  password?: string | null,
  page: number = 1,
  itemsPerPage: number = 50,
  status?: string,
  severity?: string,
  pluginId?: string,
  search?: string
) {
  return useQuery({
    queryKey: [
      "shared-scan-results",
      token,
      password,
      page,
      itemsPerPage,
      status,
      severity,
      pluginId,
      search,
    ],
    queryFn: () =>
      getSharedScanResults(
        token,
        password ?? undefined,
        page,
        itemsPerPage,
        status,
        severity,
        pluginId,
        search
      ),
    enabled: !!token,
  });
}
